/**
 * Hierarchy node components (Sprint 4.3.1, PROD-650).
 *
 * Custom React Flow node renderers for the Awell 3-tier ontology:
 *   - `track` -> rounded container; declarative + activated by triggers.
 *   - `step`  -> rectangle (default) or diamond (`isDecision: true`).
 *   - `action`-> small icon-only embedded mini-node inside a Step,
 *               discriminated by `category: 'user' | 'system'`.
 *
 * Reference: `awell/studio/docs/getting-started/design-ontology.md`
 * (vault mirror under
 * `03-knowledge-base/external-references/awell/studio/docs/
 * getting-started/design-ontology.md`).
 *
 * HC2 token-only: every color resolves through Signal DS Tailwind
 * tokens (`signal-primary-*`, `surface-*`, `text-*`). No raw hex.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: each node sets `role`, `aria-label`, and `data-testid` so
 * tests + screen-reader users can identify the visual discriminator.
 */
import type {
  ActionCategory,
  StepCategory,
} from '../../services/pathway-layout';
import { MultiplicityBadge } from './MultiplicityBadge';
import { StepCategoryBadge } from './StepCategoryBadge';
import { StepStatsRow } from './StepStatsRow';

export interface HierarchyNodeData {
  label: string;
  description?: string;
  category?: ActionCategory;
  isDecision?: boolean;
  triggerType?: string;
  triggerFilter?: string;
  /**
   * Sprint 4.3.2 (PROD-651). Runtime-mode-only badge data: count of FHIR
   * Tasks instantiating this Action's def. Hidden in Design Mode (the
   * canvas does not pass this field through). When `>= 2`, the
   * <MultiplicityBadge> renders alongside the Action icon.
   */
  multiplicityCount?: number;
  /**
   * Sprint 4.3.5 canvas visual polish. Step-only fields surfacing the
   * Awell-Studio-style category pill + bottom stats row. All optional;
   * when absent the badge / stats are hidden so existing fixtures stay
   * visually unchanged.
   */
  stepCategory?: StepCategory;
  stepCategoryLabel?: string;
  programDuration?: string;
  /**
   * Pre-computed action / stakeholder / location counts for the
   * StepStatsRow at the bottom of a Step card.
   */
  actionCount?: number;
  stakeholderCount?: number;
  locationCount?: number;
}

interface NodeProps {
  id: string;
  data: HierarchyNodeData;
  selected?: boolean;
}

/**
 * Track node. Rounded container with a dashed border tint, distinct from
 * the solid Step card. The trigger picker lives on the entry edge; this
 * body surfaces the trigger summary so the canvas reads at a glance.
 *
 * Sprint 4.3.5 visual polish: tint background + dashed border so the
 * Track reads as a "containing region" rather than a peer node, matching
 * the Awell Studio canvas reference.
 */
export function TrackNode({ id, data, selected }: NodeProps) {
  return (
    <div
      role="group"
      aria-label={`Track ${data.label}`}
      data-testid={`pathway-node-track-${id}`}
      data-node-type="track"
      data-selected={selected ? 'true' : 'false'}
      className={[
        'flex min-w-[200px] flex-col gap-1 rounded-signal-lg border-2 border-dashed bg-surface-muted px-gutter-md py-gutter-sm',
        selected
          ? 'border-signal-primary-500 ring-2 ring-signal-primary-200'
          : 'border-surface-border',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="text-signal-xs font-semibold uppercase tracking-wider text-signal-primary-600">
        Track
      </span>
      <span className="text-signal-base font-semibold text-text-primary">
        {data.label}
      </span>
      {data.triggerType ? (
        <span
          data-testid={`pathway-node-track-trigger-${id}`}
          className="inline-flex w-fit items-center rounded-signal-pill bg-surface-base px-gutter-sm py-0.5 text-signal-xs text-text-secondary ring-1 ring-inset ring-surface-border"
        >
          Trigger: {data.triggerType}
          {data.triggerFilter ? ` (${data.triggerFilter})` : ''}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Step node. Rounded white card with a 1px border, soft drop shadow, and
 * three regions per the Awell Studio canvas reference:
 *   - title (top, semibold)
 *   - StepCategoryBadge pill (immediately under the title; hidden if
 *     `stepCategory` is absent)
 *   - StepStatsRow (bottom; rendered only when at least one of action /
 *     stakeholder / location counts is present, separated by a top
 *     divider)
 *
 * Decision branches keep the diamond discriminator (`data-decision="true"`)
 * via CSS rotation per the Sprint 4.3.1 contract; the badge + stats row
 * still surface inside but un-rotated so they remain readable.
 */
export function StepNode({ id, data, selected }: NodeProps) {
  const isDecision = data.isDecision === true;
  const hasStats =
    typeof data.actionCount === 'number' ||
    typeof data.stakeholderCount === 'number' ||
    typeof data.locationCount === 'number';
  const stepCategoryLabel =
    data.stepCategoryLabel ?? (data.stepCategory ?? '').toUpperCase();
  return (
    <div
      role="group"
      aria-label={`Step ${data.label}`}
      data-testid={`pathway-node-step-${id}`}
      data-node-type="step"
      data-selected={selected ? 'true' : 'false'}
      data-decision={isDecision ? 'true' : 'false'}
      data-step-category={data.stepCategory ?? ''}
      className={[
        'flex min-w-[200px] flex-col gap-1 bg-surface-base shadow-signal-card',
        isDecision
          ? 'rotate-45 aspect-square items-center justify-center px-gutter-sm py-gutter-sm rounded-signal-md'
          : 'rounded-signal-lg px-gutter-md py-gutter-md',
        selected
          ? 'border-2 border-signal-primary-500'
          : 'border border-surface-border',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={
          isDecision
            ? '-rotate-45 flex flex-col items-center gap-1 text-center'
            : 'flex flex-col gap-1'
        }
      >
        <span className="text-signal-base font-semibold text-text-primary">
          {data.label}
        </span>
        {data.stepCategory ? (
          <StepCategoryBadge
            category={data.stepCategory}
            label={stepCategoryLabel}
            programDuration={data.programDuration}
            anchorId={id}
          />
        ) : (
          <span className="text-signal-xs font-semibold uppercase tracking-wider text-text-tertiary">
            {isDecision ? 'Decision' : 'Step'}
          </span>
        )}
      </div>
      {!isDecision && hasStats ? (
        <div className="mt-gutter-sm border-t border-surface-border pt-gutter-sm">
          <StepStatsRow
            actions={data.actionCount ?? 0}
            stakeholders={data.stakeholderCount ?? 0}
            locations={data.locationCount ?? 0}
            anchorId={id}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * User-action icon. Stylised human silhouette using SVG primitives so
 * the asset has zero npm-dep cost. Token-only fills.
 */
export function UserActionIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      data-testid="pathway-icon-user-action"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label="User action"
      className="text-signal-primary-600"
    >
      <circle cx="12" cy="8" r="3.5" fill="currentColor" />
      <path
        d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * System-action icon. Stylised gear/cog using SVG primitives.
 */
export function SystemActionIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      data-testid="pathway-icon-system-action"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label="System action"
      className="text-text-tertiary"
    >
      <path
        d="M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 5.5a2 2 0 110-4 2 2 0 010 4z"
        fill="currentColor"
      />
      <path
        d="M19 12c0-.5-.1-1-.2-1.4l1.6-1.2-1.6-2.8-1.9.7c-.7-.6-1.5-1.1-2.4-1.4L14.2 4h-3.4l-.3 1.9c-.9.3-1.7.8-2.4 1.4l-1.9-.7-1.6 2.8L6.2 10.6c-.1.4-.2.9-.2 1.4s.1 1 .2 1.4L4.6 14.6l1.6 2.8 1.9-.7c.7.6 1.5 1.1 2.4 1.4l.3 1.9h3.4l.3-1.9c.9-.3 1.7-.8 2.4-1.4l1.9.7 1.6-2.8-1.6-1.2c.1-.4.2-.9.2-1.4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}

/**
 * Action node. Small icon-only embedded mini-node intended to render
 * INSIDE a Step container (React Flow `parentNode`). The user vs system
 * discriminator is reflected in:
 *   - the icon (human silhouette vs gear),
 *   - the ring color (`signal-primary` vs `surface-muted`),
 *   - the `data-category` attribute for tests + the inspector binding.
 *
 * The label is rendered as a tooltip + screen-reader-only text so the
 * node stays icon-only on the canvas per the spec.
 */
export function ActionNode({ id, data, selected }: NodeProps) {
  const category: ActionCategory = data.category ?? 'system';
  const isUser = category === 'user';
  const multiplicityCount =
    typeof data.multiplicityCount === 'number' ? data.multiplicityCount : 0;
  const showBadge = multiplicityCount >= 2;
  return (
    <div
      role="button"
      aria-label={`${isUser ? 'User' : 'System'} action: ${data.label}`}
      title={`${isUser ? 'User' : 'System'} action: ${data.label}`}
      data-testid={`pathway-node-action-${id}`}
      data-node-type="action"
      data-category={category}
      data-selected={selected ? 'true' : 'false'}
      data-multiplicity-count={multiplicityCount}
      className={[
        'inline-flex items-center gap-1 rounded-full px-1 py-0.5 ring-1 ring-inset shadow-signal-card',
        isUser
          ? 'bg-signal-primary-100 ring-signal-primary-300'
          : 'bg-surface-muted ring-surface-border',
        selected ? 'outline outline-2 outline-signal-primary-500' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full">
        {isUser ? <UserActionIcon /> : <SystemActionIcon />}
      </span>
      <span className="sr-only">{data.label}</span>
      {showBadge ? (
        <MultiplicityBadge
          actionLabel={data.label}
          count={multiplicityCount}
          anchorId={id}
        />
      ) : null}
    </div>
  );
}

/**
 * React Flow `nodeTypes` map. Pass directly to `<ReactFlow nodeTypes>`
 * so the canvas registers the custom renderers. The map name is stable
 * across renders so React Flow treats it as referentially stable.
 */
export const HIERARCHY_NODE_TYPES_MAP = {
  track: TrackNode,
  step: StepNode,
  action: ActionNode,
} as const;
